/**
 * Black Forest Flowers — delivery details validation.
 *
 * Capture is native: the inputs rendered by snippets/bff-delivery-fields.liquid post with the
 * add-to-cart form (via the HTML5 `form=` attribute) or with the cart form. This file only adds
 * ENFORCEMENT on top of that — it is not required for the data to reach the order.
 *
 * Listeners are registered at the DOCUMENT in the CAPTURE phase so they run before Minimog's own
 * at-target listeners on the form (registration order would otherwise put us second, and the theme's
 * ProductForm would have already fired). This also lets us cover two paths Minimog's own
 * validateForm() misses:
 *   - the sticky add-to-cart bar, whose form is outside `.m-main-product--info`, so the theme's
 *     validateForm(null) returns [] and waves it straight through;
 *   - the Sunday rule, which the theme knows nothing about.
 */
(function () {
  'use strict';

  var SUNDAY = 0;
  var SUNDAY_MESSAGE =
    'We do not deliver on Sundays. Please choose another day — Sunday orders are delivered on Monday.';
  var PAST_MESSAGE = 'Please choose today or a later date.';
  var REQUIRED_MESSAGE = 'This field is required.';

  /**
   * Parse a YYYY-MM-DD value as a LOCAL date.
   * `new Date('2026-07-12')` is parsed as UTC midnight, which in a negative-offset timezone
   * (the shop is America/New_York) resolves to the PREVIOUS day locally — that would shift
   * getDay() by one and reject Mondays while allowing Sundays.
   */
  function parseLocalDate(value) {
    var parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!parts) return null;
    return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }

  /**
   * The earliest date the shop will accept, as a plain YYYY-MM-DD string.
   *
   * Read it from the input's `min`, which Liquid rendered in the SHOP's timezone
   * (America/New_York) — never from the browser's clock. The shopper may be anywhere: someone in
   * Auckland ordering flowers for a relative in Florida is already on "tomorrow" locally while New
   * York is still on today, and a browser-side `new Date()` would reject a date the shop considers
   * perfectly valid. Comparing YYYY-MM-DD strings lexicographically is exact and needs no timezone
   * maths at all.
   */
  function earliestAllowed(field) {
    return field.getAttribute('min') || '';
  }

  function errorNodeFor(field) {
    var wrap = field.closest('.bff-df__field');
    return wrap ? wrap.querySelector('[data-bff-error]') : null;
  }

  function setError(field, message) {
    field.classList.add('bff-df__input--invalid');
    field.setAttribute('aria-invalid', 'true');
    var node = errorNodeFor(field);
    if (node) {
      node.textContent = message;
      node.hidden = false;
    }
  }

  function clearError(field) {
    field.classList.remove('bff-df__input--invalid');
    field.removeAttribute('aria-invalid');
    var node = errorNodeFor(field);
    if (node) {
      node.textContent = '';
      node.hidden = true;
    }
  }

  /** Validate one date field against the shop's delivery rules. */
  function validateDateField(field) {
    var value = (field.value || '').trim();
    if (!value) return true; // emptiness is the required-check's job

    var date = parseLocalDate(value);
    if (!date) {
      setError(field, PAST_MESSAGE);
      return false;
    }

    // String compare against the shop-timezone `min`; both are YYYY-MM-DD, so this is exact.
    var min = earliestAllowed(field);
    if (min && value < min) {
      setError(field, PAST_MESSAGE);
      return false;
    }

    // Timezone-independent: the date was built from its own Y/M/D parts, so getDay() is the weekday
    // of that calendar date no matter where the shopper is.
    if (date.getDay() === SUNDAY) {
      setError(field, SUNDAY_MESSAGE);
      return false;
    }

    clearError(field);
    return true;
  }

  /**
   * Validate every field in a container.
   * @returns {HTMLElement|null} the first invalid field, or null when the container is valid.
   */
  function validateContainer(container) {
    if (!container) return null;
    var firstInvalid = null;

    container.querySelectorAll('[data-bff-required]').forEach(function (field) {
      if (!(field.value || '').trim()) {
        setError(field, REQUIRED_MESSAGE);
        if (!firstInvalid) firstInvalid = field;
      } else {
        clearError(field);
      }
    });

    container.querySelectorAll('[data-bff-date]').forEach(function (field) {
      if (!(field.value || '').trim()) return; // already flagged by the required pass
      if (!validateDateField(field) && !firstInvalid) firstInvalid = field;
    });

    return firstInvalid;
  }

  function productContainer() {
    return document.querySelector('[data-bff-delivery-fields][data-bff-context="product"]');
  }

  function cartContainer() {
    return document.querySelector('[data-bff-delivery-fields][data-bff-context="cart"]');
  }

  function reject(firstInvalid) {
    firstInvalid.focus({ preventScroll: true });
    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Which container guards this form?
   * A /cart/add form is guarded by the product fields when they exist on the page. When they do not
   * (quick view, an app's own form), we let it through on purpose — the cart gate is the backstop.
   */
  function containerForForm(form) {
    if (!form || typeof form.getAttribute !== 'function') return null;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') !== -1) return productContainer();
    if (/\/cart(\?|$)/.test(action)) return cartContainer();
    return null;
  }

  /**
   * Keep empty optional fields off the order.
   * A blank textarea still posts as "Card message": "", which litters the florist's order view with
   * empty rows. Disabled controls are excluded from FormData, so we disable the empty optional ones
   * for the duration of this submit and re-enable them on the next task. The theme builds its
   * FormData synchronously inside its own submit handler, which runs after this one, so it sees them
   * disabled; the shopper never sees a greyed-out field.
   */
  function dropEmptyOptionalFields(container) {
    var dropped = [];
    container.querySelectorAll('[name]').forEach(function (field) {
      if (field.hasAttribute('data-bff-required')) return;
      if ((field.value || '').trim()) return;
      field.disabled = true;
      dropped.push(field);
    });
    if (!dropped.length) return;
    setTimeout(function () {
      dropped.forEach(function (field) {
        field.disabled = false;
      });
    }, 0);
  }

  document.addEventListener(
    'submit',
    function (event) {
      var container = containerForForm(event.target);
      if (!container) return;
      var firstInvalid = validateContainer(container);
      if (firstInvalid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        reject(firstInvalid);
        return;
      }
      dropEmptyOptionalFields(container);
    },
    true
  );

  /**
   * Accelerated checkout buttons (Shop Pay / PayPal / Google Pay) never fire a submit event —
   * they hand off straight to Shopify. Guard them on click, in the capture phase.
   */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var onProduct = target.closest('.m-product-dynamic-checkout');
      var onCart = target.closest('.additional-checkout-buttons');
      if (!onProduct && !onCart) return;

      var container = onProduct ? productContainer() : cartContainer();
      var firstInvalid = validateContainer(container);
      if (!firstInvalid) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      reject(firstInvalid);
    },
    true
  );

  /** Live feedback: clear/repair the message as the shopper types or picks a date. */
  document.addEventListener('change', function (event) {
    var field = event.target;
    if (!field || typeof field.matches !== 'function') return;
    if (field.matches('[data-bff-date]')) {
      validateDateField(field);
      return;
    }
    if (field.matches('[data-bff-required]') && (field.value || '').trim()) clearError(field);
  });

  document.addEventListener('input', function (event) {
    var field = event.target;
    if (!field || typeof field.matches !== 'function') return;
    if (field.matches('[data-bff-required]') && (field.value || '').trim()) {
      if (!field.matches('[data-bff-date]')) clearError(field);
    }
  });
})();
